/* guide U2 pick-list 공통: 가격·배지·제품본문 헬퍼 (AiTopPicks · CartConfirmPreview · SimpleProductCard) */

import {
  fallbackProductImage,
  handleProductImageError,
  normalizeProductImage,
} from "../../lib/fallback-image";
import { useLabel } from "../../lib/labels";
import { formatAmount, formatMoney } from "../../lib/money";
import { ProductNameText } from "../../lib/ProductNameText";
import { productDisplayName } from "../../lib/productText";
import { useSkuCopyToast } from "../../lib/skuCopyToast";
import {
  pickBadgeLabel,
  type PickBadgeEntry,
  resolveSaleTags,
  OMS_SALE_TAG_I18N,
} from "./saleTagNeo";

export { pickBadgeLabel, resolveSaleTags };
export type { PickBadgeEntry };

export interface PickPriceBlock {
  amount?: number;
  originalAmount?: number;
  currency?: string;
  symbol?: string;
  discountPct?: number;
}

const _FALLBACK_CURRENCY = "£";

export function pickPriceAmt(
  p: PickPriceBlock | number | undefined,
): number | undefined {
  if (p == null) return undefined;
  if (typeof p === "number") return p;
  return p.amount;
}

export function pickPriceSym(p: PickPriceBlock | number | undefined): string {
  if (p && typeof p === "object" && p.symbol) return p.symbol;
  return _FALLBACK_CURRENCY;
}

export function pickFmt(amount: number | undefined, sym: string): string {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) return "—";
  return formatMoney(amount, sym);
}

export function pickFmtAmount(amount: number | undefined, sym: string): string {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) return "—";
  return formatAmount(amount, sym);
}

interface PickProductPriceProps {
  price: PickPriceBlock | number | undefined;
}

/** guide: product-inner > div.product-price > price-member(del+txt) + price-purchase */
export function PickProductPrice({ price }: PickProductPriceProps) {
  const saveLabel = useLabel("save", "Save");
  const priceBlock = typeof price === "object" ? price : undefined;
  const sym = pickPriceSym(price);
  const amount = pickPriceAmt(price);
  const originalAmount = priceBlock?.originalAmount;
  const discountPct = priceBlock?.discountPct;
  const hasDiscount =
    originalAmount != null &&
    amount != null &&
    Number.isFinite(originalAmount) &&
    Number.isFinite(amount) &&
    originalAmount > amount;
  const saveAmount = hasDiscount ? originalAmount - amount! : 0;

  return (
    <div className="product-price">
      {hasDiscount && (
        <div className="price-member">
          <del>{pickFmt(originalAmount, sym)}</del>
          <span className="txt">
            {discountPct && discountPct > 0
              ? `${saveLabel} ${discountPct}%`
              : `${saveLabel} ${pickFmtAmount(saveAmount, sym)}`}
          </span>
        </div>
      )}
      <div className="price-purchase">{pickFmt(amount, sym)}</div>
    </div>
  );
}

interface PickBadgesProps {
  badges: PickBadgeEntry[];
}

/* ── 텍스트형 flag (case02: product-inner 내부, .product-tag-flag) ── */

export interface PickFlagEntry {
  text: string;
  highlighted?: boolean;
}

interface PickTextFlagsProps {
  flags: PickFlagEntry[];
}

/** OMS SaleTag i18n code → display name (PickBadges 상단 배지와 동일 해석 규칙). */
function useOmsDisplayNames(): Record<string, string> {
  const omsPreOrder = useLabel(
    OMS_SALE_TAG_I18N["sale_tag.oms.pre_order"].labelKey,
    OMS_SALE_TAG_I18N["sale_tag.oms.pre_order"].fallback,
  );
  const omsBackOrder = useLabel(
    OMS_SALE_TAG_I18N["sale_tag.oms.back_order"].labelKey,
    OMS_SALE_TAG_I18N["sale_tag.oms.back_order"].fallback,
  );
  const omsLimitedOffer = useLabel(
    OMS_SALE_TAG_I18N["sale_tag.oms.limited_offer"].labelKey,
    OMS_SALE_TAG_I18N["sale_tag.oms.limited_offer"].fallback,
  );
  return {
    "sale_tag.oms.pre_order": omsPreOrder,
    "sale_tag.oms.back_order": omsBackOrder,
    "sale_tag.oms.limited_offer": omsLimitedOffer,
  };
}

export function PickTextFlags({ flags }: PickTextFlagsProps) {
  // 되묻기 심플 carousel 은 sale_tags 를 텍스트 flag 로 받는다 — OMS name 은 i18n code 라
  // 상단 배지(PickBadges)와 동일하게 display 언어로 해석한다(Promotion/Product 는 원문 그대로).
  const omsDisplayNames = useOmsDisplayNames();
  if (flags.length === 0) return null;
  return (
    <div className="product-tag-flag">
      {flags.map((f, i) => (
        <span key={`${f.text}-${i}`} className={f.highlighted ? "highlight" : undefined}>
          {omsDisplayNames[f.text] ?? f.text}
        </span>
      ))}
    </div>
  );
}

export function PickBadges({ badges }: PickBadgesProps) {
  const omsPreOrder = useLabel(
    OMS_SALE_TAG_I18N["sale_tag.oms.pre_order"].labelKey,
    OMS_SALE_TAG_I18N["sale_tag.oms.pre_order"].fallback,
  );
  const omsBackOrder = useLabel(
    OMS_SALE_TAG_I18N["sale_tag.oms.back_order"].labelKey,
    OMS_SALE_TAG_I18N["sale_tag.oms.back_order"].fallback,
  );
  const omsLimitedOffer = useLabel(
    OMS_SALE_TAG_I18N["sale_tag.oms.limited_offer"].labelKey,
    OMS_SALE_TAG_I18N["sale_tag.oms.limited_offer"].fallback,
  );
  const omsDisplayNames: Record<string, string> = {
    "sale_tag.oms.pre_order": omsPreOrder,
    "sale_tag.oms.back_order": omsBackOrder,
    "sale_tag.oms.limited_offer": omsLimitedOffer,
  };

  if (badges.length === 0) return null;
  const resolved = resolveSaleTags(badges);
  if (resolved.length === 0) return null;
  return (
    <div className="pick-tag">
      {resolved.map((t, i) => {
        const displayName = omsDisplayNames[t.name] ?? t.name;
        return (
        <span
          key={`${t.name}-${i}`}
          className={t.tagClass || undefined}
          style={!t.tagClass ? { color: "black" } : undefined}
          dangerouslySetInnerHTML={{ __html: displayName }}
        />
        );
      })}
    </div>
  );
}

/* ── 제품 본문 (.pick-product): 썸네일 + 텍스트형 flag + SKU복사 + 제품명 + 가격 ──
   guide case02 마크업. 장바구니 되묻기(AddCartPickItem)와 되묻기 심플카드(SimpleProductCard)가
   동일 카드 본문을 공유한다. CTA(.pick-btns)는 호출 측에서 붙인다. */

interface PickProductBodyProps {
  sku: string;
  name?: string;
  image?: string | null;
  price?: PickPriceBlock | number;
  flags?: PickFlagEntry[];
}

export function PickProductBody({
  sku,
  name,
  image,
  price,
  flags = [],
}: PickProductBodyProps) {
  const { copySku } = useSkuCopyToast();
  const displayName = productDisplayName(name, sku);

  return (
    <div className="pick-product">
      <div className="product-img">
        <img
          src={image ? normalizeProductImage(image) : fallbackProductImage()}
          alt={displayName}
          loading="lazy"
          draggable={false}
          onError={handleProductImageError}
        />
      </div>
      <div className="product-inner">
        {flags.length > 0 && <PickTextFlags flags={flags} />}
        <div className="product-sku">
          <button
            type="button"
            onClick={() => sku && copySku(sku)}
            title={`Copy SKU: ${sku}`}
          >
            {sku}
          </button>
        </div>
        <div className="product-ufn">
          <ProductNameText name={name} sku={sku} />
        </div>
        <PickProductPrice price={price} />
      </div>
    </div>
  );
}
